import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";

import { CreateGroupPatientDTO, GroupPatient, MAX_GROUP_SIZE, MIN_GROUP_SIZE } from "../domain/GroupPatient";
import { GroupPatientAPI } from "../infrastructure/api/GroupPatientApiService";

const GROUPS_KEY = ["group-patients"] as const;
const groupKey = (id: string) => ["group-patients", id] as const;

/**
 * The server's refusal, in words the therapist can act on.
 *
 * Takes the caller's `t` rather than the i18n singleton, so the scheduler and
 * every other module that lists groups does not pull i18n setup in with them.
 */
export function groupErrorMessage(error: unknown, t: TFunction): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail;
    switch (detail) {
        case "group_mixed_ownership":
            return t("patient.group.mixed");
        case "group_size_out_of_range":
            return t("patient.group.sizeError", { min: MIN_GROUP_SIZE, max: MAX_GROUP_SIZE });
        case "group_not_found":
            return t("patient.group.notFound");
        default:
            return t("patient.group.errorGeneric");
    }
}

export function useGroupPatients(enabled = true) {
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: GROUPS_KEY,
        queryFn: async () => (await GroupPatientAPI.list()).data,
        enabled,
    });
    return { groups: data ?? [], loading: isLoading, failed: isError, refresh: refetch };
}

/** The caller's groups this patient is a member of. */
export function usePatientGroups(patientId?: string, enabled = true) {
    const { groups, loading } = useGroupPatients(enabled && Boolean(patientId));
    return {
        groups: groups.filter((group) => group.members.some((member) => member.id === patientId)),
        loading: enabled && Boolean(patientId) && loading,
    };
}

export function useGroupPatient(id?: string) {
    const { data, isLoading, isError } = useQuery({
        queryKey: groupKey(id ?? ""),
        queryFn: async () => (await GroupPatientAPI.get(id as string)).data,
        enabled: Boolean(id),
        retry: false,
    });
    return { group: data ?? null, loading: isLoading, failed: isError };
}

/** Every change to a group answers with the group as it now is. */
function useGroupWrite<TVariables>(write: (variables: TVariables) => Promise<GroupPatient>) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: write,
        onSuccess: (group) => {
            queryClient.setQueryData(groupKey(group.id), group);
            queryClient.invalidateQueries({ queryKey: GROUPS_KEY, exact: true });
        },
    });
}

export function useCreateGroupPatient() {
    return useGroupWrite(async (data: CreateGroupPatientDTO) => (await GroupPatientAPI.create(data)).data);
}

export function useRenameGroupPatient(id: string) {
    return useGroupWrite(
        async (displayName: string | null) => (await GroupPatientAPI.rename(id, displayName)).data
    );
}

export function useAddGroupMember(id: string) {
    return useGroupWrite(
        async (patientId: string) => (await GroupPatientAPI.addMember(id, patientId)).data
    );
}

export function useRemoveGroupMember(id: string) {
    return useGroupWrite(
        async (patientId: string) => (await GroupPatientAPI.removeMember(id, patientId)).data
    );
}

export function useDeleteGroupPatient() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await GroupPatientAPI.delete(id);
            return id;
        },
        onSuccess: (id) => {
            queryClient.removeQueries({ queryKey: groupKey(id) });
            queryClient.invalidateQueries({ queryKey: GROUPS_KEY, exact: true });
        },
    });
}
